@if (loadingCreateDocument()) {
    <ra-full-page-loading [message]="'order.details.creatingDocument' | translate" />
}

@if (!orderMainService.currentOrder()) {
    <div class="container-message-empty">
        <ra-empty
            animate.enter="empty-enter"
            [title]="'order.details.emptyTitle' | translate"
            [message]="'order.details.emptyMessage' | translate"
        />
    </div>
}
@else {

    @let order = orderMainService.currentOrder();

    <!-- O cabeçalho é recriado (track _id) ao trocar de serviço, disparando a
         animação de entrada. Editar o mesmo serviço (mesmo _id) não re-dispara. -->
    @for (o of [order]; track o._id) {

    <!-- ====== Header (hero) ====== -->
    <div class="container-header" animate.enter="reveal">

        <div class="header-main">

            <div class="container-identity">

                <div class="identity-avatar">{{ clientInitials() }}</div>

                <div class="container-titles">
                    <div class="title-row">
                        <h1 class="ra-label-m bold">{{ order?.client?.name }}</h1>

                        <!-- Código da ordem de serviço: identificação curta que o cliente
                             usa para se referir à OS (e pela qual a busca também casa).
                             O selo inteiro copia — é o número que se dita no telefone. -->
                        @if (order?.code) {
                            <button
                                type="button"
                                class="identity-code"
                                [class.copied]="codeCopied()"
                                [attr.aria-label]="'order.common.copyCode' | translate"
                                [raTooltip]="{
                                    text: (codeCopied()
                                        ? ('order.common.codeCopied' | translate)
                                        : ('order.common.copyCode' | translate)),
                                    position: 'top'
                                }"
                                (click)="copyCode()">
                                {{ 'order.common.codeLabel' | translate: { code: order?.code } }}
                                <ra-icon
                                    [icon]="codeCopied() ? 'tick' : 'copy'"
                                    varColor="currentColor"
                                    [size]="14"
                                />
                            </button>
                        }
                    </div>

                    <!-- Clicar na descrição abre o editor, então é um botão de verdade:
                         como `<p>` não recebia foco, editar a descrição era impossível
                         só com o teclado. -->
                    <button
                        type="button"
                        class="ra-label-xs content-secondary identity-description"
                        [attr.aria-label]="'order.details.editDescriptionTooltip' | translate"
                        (click)="isEditingDescription.set(true)">
                        {{ order?.description || ('order.common.noDescription' | translate) }}
                    </button>
                </div>
            </div>

            <div class="container-actions">

                <!-- Equipe: ícone com o selo da contagem, no mesmo par do pacote pronto.
                     Abre a gestão de membros; era um card inteiro na fileira de baixo. -->
                <button
                    type="button"
                    class="header-icon-button"
                    [disabled]="loadingMembers()"
                    [attr.aria-label]="'order.details.teamButtonAria' | translate: { count: (order?.employees || []).length }"
                    [raTooltip]="{ text: ('order.details.teamButton' | translate), position: 'bottom' }"
                    (click)="isModalMembersVisible.set(true)">
                    <ra-icon icon="profile-2user" varColor="contentPrimary" [size]="18" />
                    <span class="header-icon-button__badge header-icon-button__badge--count" aria-hidden="true">
                        {{ (order?.employees || []).length }}
                    </span>
                </button>

                <!-- Pacote pronto: um ícone com o selo da contagem, ao lado do botão de opções.
                     O texto e as ações vivem no menu. O aviso persistente continua aqui; a ilha
                     do rodapé é passageira. -->
                @if (batchService.ready() && batchService.batch()?.orderId === order?._id) {
                    <div
                        class="batch-ready"
                        clickOutside
                        [clickOutsideEnabled]="isBatchMenuVisible()"
                        (clickOutside)="isBatchMenuVisible.set(false)">
                        <button
                            type="button"
                            class="header-icon-button"
                            [attr.aria-label]="'order.documentBatch.pillReady' | translate: { count: batchService.succeeded() }"
                            [attr.aria-expanded]="isBatchMenuVisible()"
                            [raTooltip]="{ text: ('order.documentBatch.pillReady' | translate: { count: batchService.succeeded() }), position: 'bottom' }"
                            (click)="isBatchMenuVisible.set(!isBatchMenuVisible())">
                            @if (batchService.downloading()) {
                                <i class="header-icon-button__spinner" aria-hidden="true"></i>
                            } @else {
                                <ra-icon icon="document-download" varColor="contentPrimary" [size]="18" />
                            }
                            <span class="header-icon-button__badge header-icon-button__badge--ready" aria-hidden="true">{{ batchService.succeeded() }}</span>
                        </button>

                        @if (isBatchMenuVisible()) {
                            <ra-menu [items]="batchMenuItems()" [width]="288" (select)="onBatchMenuSelect($event)" />
                        }
                    </div>
                }

                @if (loadingActions()) {
                    <div class="container-loading-actions">
                        <p>{{ loadingActionsMessage() || ('order.common.wait' | translate) }}</p>
                        <ra-loading [size]="'small'" />
                    </div>
                }

                <!-- Botão único "Opções do serviço": abre o menu com todas as ações. -->
                <div
                    class="container-actions-menu"
                    clickOutside
                    [clickOutsideEnabled]="isActionsMenuVisible()"
                    (clickOutside)="isActionsMenuVisible.set(false)">

                    <ra-button
                        [id]="'button-order-options'"
                        [icon]="'setting'"
                        [label]="'order.details.optionsButton' | translate"
                        [type]="'primary'"
                        (click)="openActionsMenu()"
                    />

                    @if (isActionsMenuVisible()) {
                        <ra-menu [items]="actionsMenuItems()" (select)="onActionSelect($event)" />
                    }
                </div>
            </div>
        </div>

        <!-- Rodapé do cabeçalho: datas e contagem, que antes eram três cards numa
             fileira própria. Uma faixa só, presa ao card da identidade. -->
        <div class="header-foot">
            <span class="foot-item">
                <span class="foot-icon foot-icon--created">
                    <ra-icon icon="calendar" varColor="accentBlue" [size]="14" />
                </span>
                {{ 'order.details.footCreated' | translate }}
                <strong>{{ order?.createdAt | date:'short' }}</strong>
            </span>
            <span class="foot-item">
                <span class="foot-icon foot-icon--updated">
                    <ra-icon icon="edit" varColor="accentOrange" [size]="14" />
                </span>
                {{ 'order.details.footUpdated' | translate }}
                <strong>{{ order?.updatedAt | date:'short' }}</strong>
            </span>
            <span class="foot-item">
                <span class="foot-icon foot-icon--documents">
                    <ra-icon icon="folder" varColor="accentGreen" [size]="14" />
                </span>
                {{ 'order.details.footDocuments' | translate }}
                <strong>{{ documents().length }}</strong>
            </span>
        </div>
    </div>

    }

    <!-- ====== Documents ====== -->
    <div class="container-documents">

        <div class="container-header-documents">

            <div>
                <h2 class="ra-label-s bold">{{ 'order.details.documentsTitle' | translate }}</h2>
                <p class="ra-label-xs content-secondary">{{ 'order.details.documentsSubtitle' | translate }}</p>
            </div>

            <div class="ra-flex-center-between gap-16 header-documents-actions">

                <!-- Forma de visualização: cards ou agrupado por mês. A escolha é do
                     usuário (vale para todas as ordens de serviço) e vai para a API,
                     então o app abre na mesma visão. -->
                @if (documents().length > 0) {
                    <div
                        class="view-switch"
                        role="group"
                        [attr.aria-label]="'order.details.viewLabel' | translate">
                        <div class="view-switch__options">
                            @for (option of viewOptions; track option.value) {
                                <button
                                    type="button"
                                    class="view-switch__option"
                                    [attr.aria-pressed]="documentsView() === option.value"
                                    [testId]="'documents-view-' + option.value"
                                    (click)="setDocumentsView(option.value)">
                                    <ra-icon
                                        [icon]="option.icon"
                                        varColor="currentColor"
                                        [size]="15"
                                    />
                                    {{ option.label | translate }}
                                </button>
                            }
                        </div>
                    </div>
                }

                <ra-button
                    [id]="'button-use-model'"
                    [label]="'order.details.yourTemplates' | translate"
                    [type]="'primary-outline'"
                    (click)="isModalListTemplatesVisible.set(true)"
                />

                <div class="container-new-document">
                    @if (!featureFlagController.isBlocked('contracts')) {
                        <ra-button
                            [id]="'button-new-document'"
                            [label]="'order.common.new' | translate"
                            [type]="'primary'"
                            (click)="isCreateDocumentOptionsVisible.set(true)"
                        />
                    } @else {
                        <ra-button
                            [id]="'button-new-document'"
                            [label]="'order.details.newDocument' | translate"
                            [type]="'primary'"
                            (click)="createBlankDocument()"
                        />
                    }
                </div>

            </div>
        </div>

        @if (loadingDocuments()) {
            <div class="container-grid">
                @for (skeleton of documentSkeletons; track $index) {
                    <document-card-skeleton />
                }
            </div>
        }
        @else if (documents().length === 0) {
            <ra-empty
                animate.enter="empty-enter"
                [marginTop]="64"
                [title]="'order.details.noDocumentsTitle' | translate"
                [message]="'order.details.noDocumentsMessage' | translate"
            />
        }
        @else if (documentsView() === documentsViewEnum.MONTH) {
            <!-- Por mês: um cabeçalho por mês (recolhível) e cards leves, sem borda,
                 dentro de cada grupo. Os 3 meses mais recentes abrem expandidos. -->
            <div class="container-months" (scroll)="closeDocumentMenu()">
                @for (group of documentsByMonth(); track group.key; let g = $index) {
                    <details class="month-group" [open]="g < 3">
                        <summary class="month-group__header">
                            <ra-icon class="month-group__chevron" icon="arrow-right" varColor="contentTertiary" [size]="14" />
                            <span class="month-group__title">{{ group.date | date:'MMMM y' | titlecase }}</span>
                            <span class="month-group__line"></span>
                            <span class="month-group__count">
                                {{ (group.documents.length === 1 ? 'order.details.monthCountOne' : 'order.details.monthCountOther') | translate: { count: group.documents.length } }}
                            </span>
                        </summary>
                        <div class="month-group__items">
                            @for (document of group.documents; track document._id; let i = $index) {
                                <ng-container *ngTemplateOutlet="documentCard; context: { $implicit: document, index: i, tile: true }" />
                            }
                        </div>
                    </details>
                }
            </div>
        }
        @else {
            <div class="container-grid" (scroll)="closeDocumentMenu()">
                @for (document of documents(); track document._id; let i = $index) {
                    <ng-container *ngTemplateOutlet="documentCard; context: { $implicit: document, index: i, tile: false }" />
                }
            </div>
        }
    </div>
}

<!-- Card do documento: o MESMO nas duas visões. Em "cards" tem borda e sobe no
     hover; em "por mês" (tile) vira uma linha leve sobre fundo sutil. As opções
     (renomear, duplicar, mover, remover...) abrem pelo ⋯ ou pelo clique direito. -->
<ng-template #documentCard let-document let-i="index" let-tile="tile">

    @let isProcessing = document?.status === documentStatusEnum.PENDING || document?.status === documentStatusEnum.CREATING;
    @let updatedAt = (document?.contentUpdatedAt ?? document?.createdAt) | date:(tile ? 'shortDate' : 'short') | titlecase;
    @let sizeGB = document?.sessions | documentSizeGB | formatFileSize:'GB';

    <div
        class="document-card"
        role="button"
        animate.enter="card-enter"
        [attr.tabindex]="isProcessing ? -1 : 0"
        [class.document-card--tile]="tile"
        [style.--enter-delay]="(i % 12) * 35 + 'ms'"
        [class.processing]="isProcessing"
        [class.menu-open]="documentMenu()?.document?._id === document._id"
        [testId]="'document-card-' + i"
        (click)="!isProcessing && documentController.navigateToDocument(document)"
        (keydown.enter)="!isProcessing && documentController.navigateToDocument(document)"
        (contextmenu)="onDocumentContextMenu($event, document)">

        <div
            class="card-icon"
            [class.ai-processing]="isProcessing"
            [style.background-color]="isProcessing ? 'var(--primaryLight)' : 'var(--' + (document?.type | iconColorByDocumentType) + 'Light)'">
            <ra-icon
                [icon]="isProcessing ? 'magicpen' : (document?.type | iconByDocumentType)"
                [varColor]="isProcessing ? 'primary' : (document?.type | iconColorByDocumentType)"
                [size]="tile ? 16 : 20"
            />
        </div>

        <div class="card-body">
            <div class="card-title-row">
                <p class="card-title">{{ document?.title }}</p>

                <!-- Falha na geração tem prioridade sobre a tag "IA": é o que o
                     usuário precisa ver no card (mesmo selo do feed) -->
                @if (document?.status === documentStatusEnum.ERROR) {
                    <ra-badge
                        [label]="'order.feed.generationError' | translate"
                        [variant]="'danger'"
                        [size]="'small'"
                    />
                }
                <!-- Tag "IA" para documentos gerados por IA (resumo do serviço ou geração via chat) -->
                @else if (isGeneratedByIa(document)) {
                    <ra-badge
                        [label]="'order.details.iaTag' | translate"
                        [variant]="'primary'"
                        [icon]="'magicpen'"
                        [size]="'small'"
                    />
                }
            </div>

            <p class="card-meta">{{ updatedAt }}@if (!isProcessing) { · {{ sizeGB }}}</p>

            <!-- Progresso PERSISTIDO do import em geração (mesma barra do feed):
                 sobrevive a reload e aparece igual em outro aparelho. -->
            @if (isProcessing) {
                @let generationProgress = generationPercent(document);
                @if (generationProgress !== null) {
                    <ra-progress-bar
                        class="card-progress"
                        [value]="generationProgress"
                        [label]="generationStageLabel(document)"
                        [showValue]="true"
                    />
                }
            }

            <!-- Progresso do checklist (somente em modo checklist) -->
            @if (document?.config?.checklistMode) {
                @let progress = document?.sessions | checklistCompletion;
                <ra-progress-bar
                    class="card-progress"
                    [value]="progress.percent"
                    [variant]="progress.percent === 100 ? 'success' : 'primary'"
                    [showValue]="true"
                    [ariaLabel]="'order.details.documentsTitle' | translate"
                />
            }
        </div>

        <div class="card-end">
            @if (isProcessing) {
                <ra-loading [size]="'small'" />
            } @else {
                <!-- Enter no ⋯ não pode subir até o card (que abriria o documento) -->
                <button
                    type="button"
                    class="card-more"
                    aria-haspopup="menu"
                    [attr.aria-expanded]="documentMenu()?.document?._id === document._id"
                    [attr.aria-label]="'order.details.documentMenu.label' | translate: { title: document?.title }"
                    [testId]="'document-card-more-' + i"
                    (click)="onDocumentMoreClick($event, document)"
                    (keydown.enter)="$event.stopPropagation()">
                    <ra-icon icon="more" varColor="contentSecondary" [size]="tile ? 16 : 18" />
                </button>
            }
        </div>
    </div>
</ng-template>

<!-- Menu do documento: um só para a lista toda, num overlay (a área dos cards
     rola e tem overflow: hidden, então um menu dentro do card seria cortado). -->
<ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="documentMenuOrigin()"
    [cdkConnectedOverlayOpen]="!!documentMenu()"
    [cdkConnectedOverlayPositions]="documentMenuPositions()"
    [cdkConnectedOverlayViewportMargin]="8"
    (overlayOutsideClick)="onDocumentMenuOutsideClick($event)"
    (overlayKeydown)="onDocumentMenuKeydown($event)"
    (attach)="focusDocumentMenu()"
    (detach)="closeDocumentMenu()">
    <ra-menu
        testId="document-menu"
        [items]="documentMenuItems()"
        [anchored]="false"
        (select)="onDocumentMenuSelect($event)"
        (contextmenu)="$event.preventDefault()"
    />
</ng-template>

@if (documentToRename(); as document) {
    <ra-modal-edit-text
        [title]="'order.details.documentMenu.renameTitle' | translate"
        [placeholder]="'order.details.documentMenu.renamePlaceholder' | translate"
        [value]="document.title || ''"
        [limitCharacters]="150"
        [modalSize]="'medium'"
        [editorHeight]="120"
        (close)="documentToRename.set(null)"
        (save)="renameDocument($event)"
    />
}

@if (documentToMove(); as document) {
    <ra-modal-list-orders
        [message]="'order.details.documentMenu.moveMessage' | translate: { title: document.title }"
        [excludeOrderId]="orderMainService.currentOrder()?._id"
        (close)="documentToMove.set(null)"
        (selectOrder)="moveDocument($event)"
    />
}

@if (isEditingDescription()) {
    <ra-modal-edit-text
        [value]="orderMainService.currentOrder()?.description || ''"
        [limitCharacters]="100"
        [modalSize]="'medium'"
        [editorHeight]="180"
        (close)="isEditingDescription.set(false)"
        (save)="updateDescription($event)"
    />
}

@if (isModalListClientsVisible()) {
    <ra-modal-list-clients
        [message]="'order.details.selectClientMessage' | translate"
        (close)="isModalListClientsVisible.set(false)"
        (selectClient)="selectClient($event)"
    />
}

@if (isModalMembersVisible()) {
    <!-- Auto-save: cada toggle já persiste; a modal só fecha pelo X -->
    <modal-members
        [currentEmployees]="orderMainService.currentOrder()?.employees || []"
        (close)="isModalMembersVisible.set(false)"
    />
}

@if (isCreateDocumentOptionsVisible()) {
    <create-document-options
        (createBlankDocument)="createBlankDocument()"
        (createBlankContract)="createBlankContract()"
        (useInternalTemplate)="openInternalTemplatesModal()"
        (createQuote)="createQuote()"
        (importFiles)="openImportDocuments()"
        (close)="isCreateDocumentOptionsVisible.set(false)"
    />
}

@if (isImportDocumentsVisible()) {
    <import-documents-modal
        [orderId]="orderMainService.currentOrder()?._id"
        (created)="onImportCreated($event)"
        (close)="isImportDocumentsVisible.set(false)"
    />
}

@if (isModalListTemplatesVisible()) {
    <ra-modal-list-templates
        (close)="isModalListTemplatesVisible.set(false)"
        (selectTemplate)="selectTemplate($event)"
    />
}

@if (isModalListInternalTemplatesVisible()) {
    <ra-modal-list-internal-templates
        (close)="closeInternalTemplatesModal()"
        (selectInternalTemplate)="selectInternalTemplate($event)"
    />
}

@if (isDocumentBatchVisible()) {
    <modal-document-batch
        [orderId]="orderMainService.currentOrder()?._id"
        [documents]="documents()"
        (close)="isDocumentBatchVisible.set(false)"
    />
}
